import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { BrushStart, LegendKey } from "../../../models/chart_model";
import { HarayanaDiscomData } from "../../../models/rajasthan";
import {
  getHaryanaForecast,
  getHaryanaGenerationProfile,
  getHaryanaSurplusDeficitMetrics,
} from "../../../Rest_api/RestApi";
import { MetricChipHead } from "../../../components/Metrics";
import { SurplusDeficitData } from "../../../models/metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { ExchangeColors } from "../../Dashboard/Exchange3/FormatData";
import { H_CHART_TYPES } from "../const";
import { GROUP_BY } from "./Data";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import Tooltip from "../../../components/tooltip";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import MainPageContainer from "../../../components/MainPageContainer";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import { useSelector } from "react-redux";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { HaryanaForecastSummaryAnalysis } from "./Forcast_Summary";
import { HaryanaForecastPeakAnalysis } from "./Peak_Forecast";
import { getTimeRange } from "../../Dashboard/Exchange3/Chart";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { normalizeDisplayLabelWithoutUndefined } from "../../../utility/displayText";

let Intervals = [
  {
    label: "Time Slot",
    key: "time",
    values: ["block", "date"],
    isTime: true,
  },
  {
    label: "Date",
    key: "date",
    values: ["date"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month",
    values: ["month"],
    isTime: false,
  },
];

const GENERATION_BY = [
  { label: "Cumulative", value: "cumulative" },
  { label: "Comparative", value: "comparative" },
];

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const UNITS = ["MW", "MWh", "LU"];

const SOURCES = [
  { label: "Solar", value: "solar" },
  { label: "Wind", value: "wind" },
  { label: "Hydro", value: "hydro" },
  { label: "Thermal", value: "thermal" },
  { label: "Biomass", value: "biomass" },
  { label: "Bagasse", value: "bagasse" },
  { label: "Gas", value: "gas" },
  { label: "Nuclear", value: "nuclear" },
  { label: "Hybrid", value: "hybrid" },
  { label: "Small Hydro", value: "small_hydro" },
];

function getMonthWithYear(dateString: string): string {
  const date = new Date(dateString);
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear().toString().slice(-2);
  return `${month}-${year}`;
}

function getDateRangeKey(dateString: string, dates: string): string {
  if (dates.includes(" to ")) {
    const [start, end] = dates.split(" to ");
    const startMonth = getMonthWithYear(start.trim());
    const endMonth = getMonthWithYear(end.trim());
    return startMonth === endMonth
      ? startMonth
      : `${startMonth} to ${endMonth}`;
  }
  return getMonthWithYear(dateString);
}

export function HaryanaForecast() {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const [selectedXAxisMode, setSelectedXAxisMode] = useState(X_AXIS_MODES[0]);
  const [selectedUnit, setSelectedUnit] = useState(UNITS[0]);
  const [selectedGenerationBy, setSelectedGenerationBy] = useState(
    GENERATION_BY[0],
  );
  const [selectedSources, setSelectedSources] = useState<any[]>([SOURCES[1]]);
  const [data, setData] = useState<HarayanaDiscomData[]>([]);
  const [generationData, setGenerationData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedDiscom, setSelectedDiscom] = useState<any[]>([]);
  const [selectedArea, setSelectedArea] = useState<any[]>([]);
  const [uniqueDate, setUniqueDate] = useState<any[]>([]);
  const [selectedDate, setSelectedDate] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState(GROUP_BY[1]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [loading, setLoading] = useState(true);
  const [deselectedLegends, setDeselectedLegends] = useState<string[]>([]);
  const [metrics, setMetrics] = useState<SurplusDeficitData | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const chartSuffixes = [
    { suffix: "demand", displayName: "Demand", stackSuffix: "demand" },
    {
      suffix: "actual_generation",
      displayName: "Generation",
      stackSuffix: "generation",
    },
    {
      suffix: "surplus_deficit",
      displayName: "Surplus/Deficit",
      stackSuffix: "surplus",
    },
  ];

  const generationSuffixes = [
    {
      suffix: "actual_generation",
      displayName: " ",
      stackSuffix: "generation",
    },
  ];

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    selectedInterval: { isTime: boolean },
    selectedChartType: { value: string; stackId?: string },
    ExchangeColors: string[],
    isPercentage: boolean = false,
  ) => {
    let legendName: string;

    if (selectedGenerationBy.value === "comparative") {
      legendName =
        `${normalizeDisplayLabelWithoutUndefined(key).trim()} ${displayName}`.trim();
    } else {
      legendName = displayName;
    }

    return {
      dataKey: isPercentage
        ? `${key}_${suffix}`
        : `${key}_${suffix}_${selectedUnit.toLowerCase()}`,
      name: legendName,
      legendColor: undefined,
      type: selectedChartType.value,
      stackId:
        selectedChartType.stackId !== undefined ? stackSuffix : undefined,
      unit: isPercentage ? "%" : selectedUnit,
      yAxisId: isPercentage ? "right" : undefined,
    };
  };

  const [selectedSuffix, setSelectedSuffix] = useState([chartSuffixes[0]]);

  const _handlefetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates) {
      return;
    }

    setLoading(true);

    try {
      if (selectedGenerationBy.value === "cumulative") {
        const promises = DefaultDates[activeTab].defaultTrendDates.map(
          async (date: any) => {
            try {
              const [startDate, endDate] = date.value.split(",");
              const data = await getHaryanaForecast(startDate, endDate);
              return data.map((item: any) => ({
                ...item,
                dates: date.label,
                dateRange: `${startDate} to ${endDate}`,
              }));
            } catch (error) {
              console.error(`Error processing date ${date.label}:`, error);
              return [];
            }
          },
        );

        const results = await Promise.all(promises);
        const flattenedData = results.flat();

        const dataMap = new Map<string, any>();
        flattenedData.forEach((item: any) => {
          const uniqueKey = `${item.date}_${item.block}`;
          if (!dataMap.has(uniqueKey)) {
            dataMap.set(uniqueKey, item);
          }
        });
        const newData = Array.from(dataMap.values());

        setData(newData);
        setGenerationData([]);

        setUniqueDate(
          Array.from(
            new Set(
              newData
                .map((item) => item.date)
                .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
            ),
          ),
        );
        setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
      } else {
        const sourcesParam =
          selectedSources.length > 0
            ? "&sources=" +
              selectedSources
                .map((s) =>
                  normalizeDisplayLabelWithoutUndefined(s.value.toUpperCase()),
                )
                .join(",")
            : "";

        const promises = DefaultDates[activeTab].defaultTrendDates.map(
          async (date: any) => {
            try {
              const [startDate, endDate] = date.value.split(",");
              const response = await getHaryanaGenerationProfile(
                startDate,
                endDate,
                sourcesParam,
              );
              return response.map((item: any) => ({
                ...item,
                dates: date.label,
                dateRange: `${startDate} to ${endDate}`,
                block: item.slot,
                entity_name: item.entity_name,
                source: item.source,
                actual_generation_mw: parseFloat(
                  item.actual_generation_in_mw?.toString() || "0",
                ),
                actual_generation_mwh:
                  parseFloat(item.actual_generation_in_mw?.toString() || "0") /
                  4,
                is_outage: item.is_outage,
                time_string: item.time_string,
              }));
            } catch (error) {
              console.error(`Error processing date ${date.label}:`, error);
              return [];
            }
          },
        );

        const results = await Promise.all(promises);
        const flattenedData = results.flat();

        const dataMap = new Map<string, any>();
        flattenedData.forEach((item) => {
          const uniqueKey = `${item.date}_${item.slot}_${item.entity_name}`;
          if (!dataMap.has(uniqueKey)) {
            dataMap.set(uniqueKey, item);
          }
        });
        const newData = Array.from(dataMap.values());

        setGenerationData(newData);
        setData([]);

        setUniqueDate(
          Array.from(
            new Set(
              newData
                .map((item) => item.date)
                .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()),
            ),
          ),
        );
        setUniqueBlock(Array.from(new Set(newData.map((item) => item.block))));
      }
    } catch (error) {
      console.error("Error in data fetching:", error);
      setData([]);
      setGenerationData([]);
    } finally {
      setLoading(false);
    }
  }, [DefaultDates, activeTab, selectedGenerationBy, selectedSources]);

  useEffect(() => {
    let filteredData: any[] = [];

    if (selectedGenerationBy.value === "cumulative") {
      filteredData = data.map((item: any) => ({
        ...item,
        time_key_string:
          item.block.toString() + " (" + getTimeRange(item.block) + ")",
        month: getDateRangeKey(item.date, item.dateRange || item.dates || ""),
      }));
    } else {
      filteredData = generationData.map((item: any) => ({
        ...item,
        time_key_string:
          item.block.toString() + " (" + getTimeRange(item.block) + ")",
        month: getDateRangeKey(item.date, item.dateRange || item.dates || ""),
      }));
    }

    if (selectedDate.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedDate.map((item) => item.value).includes(item.date),
      );
    }
    if (selectedBlock.length > 0) {
      filteredData = filteredData.filter((item) =>
        selectedBlock.map((item) => item.value).includes(item.block),
      );
    }

    filteredData.sort((a, b) => {
      const dateCompare =
        new Date(a.date).getTime() - new Date(b.date).getTime();
      if (dateCompare !== 0) return dateCompare;
      return a.block - b.block;
    });

    function getKeys() {
      if (selectedInterval.key === "time") {
        return ["date", "block", "time_key_string"];
      } else if (selectedInterval.key === "date") {
        return ["date"];
      } else {
        return [selectedInterval.values[0]];
      }
    }
    console.log(filteredData);
    const par: ChartDataParams = {
      keys: getKeys(),
      groupBy:
        selectedGenerationBy.value === "comparative" ? ["entity_name"] : [],
      tableKeys: ["date", "block", "month"],
      volumeKey: ``,
      aggregationKeys:
        selectedGenerationBy.value === "cumulative"
          ? [
              "demand_mw",
              "demand_mwh",
              "demand_lu",
              "actual_generation_mw",
              "actual_generation_mwh",
              "actual_generation_lu",
              "surplus_deficit_mw",
              "surplus_deficit_mwh",
              "surplus_deficit_lu",
              "surplus_deficit_percentage",
            ]
          : [
              "actual_generation_mw",
              "actual_generation_mwh",
              "actual_generation_lu",
            ],
      priceKey: "",
      averageKeys:
        selectedGenerationBy.value === "cumulative"
          ? ["demand_mw", "actual_generation_mw", "surplus_deficit_percentage"]
          : ["actual_generation_mw"],
      averageDuplicationKeys: ["date", "block", "month"],
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [
    data,
    generationData,
    selectedGroupKeys,
    selectedInterval,
    selectedDiscom,
    selectedArea,
    selectedDate,
    selectedBlock,
    selectedSuffix,
    selectedUnit,
    selectedGenerationBy,
  ]);

  useEffect(() => {
    _handlefetchData();
  }, [_handlefetchData]);

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultTrendDates?.length) {
      setMetrics(null);
      setMetricsLoading(false);
      return;
    }
    setMetricsLoading(true);

    try {
      const originalDates = DefaultDates[activeTab].defaultTrendDates;

      const ranges = originalDates
        .filter((date: any) => {
          const commaParts = date.value.split(",");
          return commaParts.length <= 2;
        })
        .map((date: any) => {
          let start: string;
          let end: string;

          if (date.value.includes(" to ")) {
            [start, end] = date.value
              .split(" to ")
              .map((s: string) => s.trim());
          } else {
            const parts = date.value.split(",").map((s: string) => s.trim());
            start = parts[0];
            end = parts[1] ?? parts[0];
          }

          const startDateStr = new Date(start).toISOString().split("T")[0];
          const endDateStr = new Date(end).toISOString().split("T")[0];

          return {
            startDate: startDateStr,
            endDate: endDateStr,
            blocks: selectedBlock.map((block: any) => block.value),
          };
        })
        .filter((range: any) => range.startDate && range.endDate);

      if (ranges.length === 0) {
        setMetrics(null);
        setMetricsLoading(false);
        return;
      }

      const metricsData = await getHaryanaSurplusDeficitMetrics({
        ranges,
        aggregated: true,
      });

      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [DefaultDates, activeTab, selectedBlock]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  function handleUnitChange(params: string) {
    setSelectedUnit(params);
  }

  function handleXAxisModeChange(item: string, index: number) {
    setSelectedXAxisMode(X_AXIS_MODES[index]);
  }

  function handleGenerationByChange(item: string, index: number) {
    setSelectedGenerationBy(GENERATION_BY[index]);
  }

  const handlexDataKey = useCallback(() => {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        if (selectedInterval.isTime) {
          return "block";
        } else {
          return selectedInterval.values[0];
        }
      }
      return "block";
    } else {
      return selectedInterval.key;
    }
  }, [
    selectedBlock.length,
    selectedInterval.isTime,
    selectedInterval.key,
    selectedInterval.values,
  ]);

  const xDataKey = useMemo(() => handlexDataKey(), [handlexDataKey]);

  function secondDataKey() {
    if (selectedInterval.isTime) {
      if (selectedBlock.length > 0) {
        return "date";
      }
      return "date";
    } else {
      return undefined;
    }
  }

  const summaryExtraKeys = selectedInterval.isTime
    ? ["time_key_string", selectedInterval.values[1]]
    : [selectedInterval.values[0]];

  const summaryExtraLabels = selectedInterval.values.map((value) =>
    value === "date" ? "Date" : value,
  );

  const handleLegendSelect = (selected: LegendKey[]) => {
    const allNames = getLegends().map((l: any) => l.name);
    if (selected.length === 0) {
      setDeselectedLegends([]);
    } else {
      const selectedNames = selected.map((l) => l.name);
      setDeselectedLegends(
        allNames.filter((name: string) => !selectedNames.includes(name)),
      );
    }
  };

  const currentLegends = getLegends() as any[];
  const selectedLegends =
    deselectedLegends.length === 0
      ? currentLegends
      : currentLegends.filter((l: any) => !deselectedLegends.includes(l.name));

  // For summary/table: use only the selected legends, or all if none selected
  const allLegendsF = currentLegends;
  const visibleLegendsF =
    selectedLegends.length > 0 && selectedLegends.length !== allLegendsF.length
      ? allLegendsF.filter((l: any) =>
          selectedLegends.some((al: any) => al.dataKey === l.dataKey),
        )
      : allLegendsF;

  const tablePar = {
    legends: visibleLegendsF,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    unit: selectedUnit,
    extraKeys: summaryExtraKeys,
    extraLabels: summaryExtraLabels,
    filename: AddDatesWithFileName({
      fileName:
        selectedGenerationBy.value === "cumulative"
          ? "Haryana Forecast Load"
          : "Haryana Generation Profile",
      dates: DefaultDates[activeTab]?.defaultTrendDates || [],
    }),
  };

  function getLegends() {
    const suffixes =
      selectedGenerationBy.value === "cumulative"
        ? chartSuffixes
        : generationSuffixes;

    const regularLegends = suffixes.flatMap(
      ({ suffix, displayName, stackSuffix }) =>
        chartKeys.map(
          (key, index) =>
            createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              selectedInterval,
              selectedChartType as any,
              ExchangeColors,
              false,
            ) as any,
        ),
    );

    if (selectedGenerationBy.value === "cumulative") {
      const percentageLegends = chartKeys.map(
        (key, index) =>
          createChartObject(
            key,
            index,
            "surplus_deficit_percentage",
            "Surplus/Deficit%",
            "surplus_pct",
            selectedInterval,
            selectedChartType as any,
            ExchangeColors,
            true,
          ) as any,
      );
      return [...regularLegends, ...percentageLegends];
    }

    return regularLegends;
  }

  if (selectedXAxisMode.value === "peak") {
    return (
      <HaryanaForecastPeakAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        metrics={metrics}
        metricsLoading={metricsLoading}
      />
    );
  }

  if (selectedXAxisMode.value === "summary") {
    return (
      <HaryanaForecastSummaryAnalysis
        onXAxisModeChange={handleXAxisModeChange}
        selectedUnit={selectedUnit}
        onUnitChange={handleUnitChange}
        metrics={metrics}
        metricsLoading={metricsLoading}
      />
    );
  }

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={[selectedXAxisMode.label]}
            onSelect={handleXAxisModeChange}
          />

          <FilterMenu
            title="Generation By"
            items={GENERATION_BY.map((item) => item.label)}
            selectedItems={[selectedGenerationBy.label]}
            onSelect={handleGenerationByChange}
          />

          {selectedGenerationBy.value === "comparative" && (
            <FilterMenu
              title="Source"
              items={SOURCES.map((item) => item.label)}
              selectedItems={selectedSources.map((item) => item.label)}
              onSelect={(item, index) => {
                const source = SOURCES[index];
                const isSelected = selectedSources.some(
                  (s) => s.value === source.value,
                );
                if (isSelected) {
                  setSelectedSources(
                    selectedSources.filter((s) => s.value !== source.value),
                  );
                } else {
                  setSelectedSources([...selectedSources, source]);
                }
              }}
            />
          )}

          <FilterMenu
            title="Intervals"
            items={Intervals.map((item) => item.label)}
            selectedItems={[selectedInterval.label]}
            onSelect={(item, index) => {
              setSelectedInterval(Intervals[index]);
            }}
          />

          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item, index) => {
              handleUnitChange(item);
            }}
          />

          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(item, index) => {
              setSelectedChartType(H_CHART_TYPES[index]);
            }}
          />

          {/* Disabled Filters */}
          <FilterMenu
            title="Discom"
            items={["UHBVN", "DHBVN"]}
            selectedItems={[]}
            disabled={true}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader />
        <div className="flex flex-wrap gap-4 my-0 justify-center">
          <MetricChipHead
            icon={<PeakIcon width={20} height={19} />}
            title="Surplus/Deficit"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.surplus_deficit_metrics?.["Max Surplus/Deficit (MW)"]
                    ?.value ?? undefined,
                unit:
                  metrics?.surplus_deficit_metrics?.["Max Surplus/Deficit (MW)"]
                    ?.unit ?? "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.surplus_deficit_metrics?.["Min Surplus/Deficit (MW)"]
                    ?.value ?? undefined,
                unit:
                  metrics?.surplus_deficit_metrics?.["Min Surplus/Deficit (MW)"]
                    ?.unit ?? "",
              },
            ]}
          />
          <MetricChipHead
            icon={<AvgIcon width={20} height={19} />}
            title="Total Generation"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.generation_metrics?.["Max Generation (MW)"]?.value ??
                  undefined,
                unit:
                  metrics?.generation_metrics?.["Max Generation (MW)"]?.unit ??
                  "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.generation_metrics?.["Min Generation (MW)"]?.value ??
                  undefined,
                unit:
                  metrics?.generation_metrics?.["Min Generation (MW)"]?.unit ??
                  "",
              },
            ]}
          />
          <MetricChipHead
            icon={<MinIcon width={20} height={19} />}
            title="Load"
            items={[
              {
                label: "Maximum",
                value:
                  metrics?.load_metrics?.["Max Load (MW)"]?.value ?? undefined,
                unit: metrics?.load_metrics?.["Max Load (MW)"]?.unit ?? "",
              },
              {
                label: "Minimum",
                value:
                  metrics?.load_metrics?.["Min Load (MW)"]?.value ?? undefined,
                unit: metrics?.load_metrics?.["Min Load (MW)"]?.unit ?? "",
              },
            ]}
          />
        </div>
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Surplus/Deficit"
              subtitle="Fetching HPPC surplus and deficit analysis data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">
                  {selectedGenerationBy.value === "cumulative"
                    ? `Surplus/Deficit Analysis (${selectedUnit})`
                    : `Surplus/Deficit Analysis (${selectedUnit})`}
                </h2>
                <div className="flex gap-x-6  justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Select Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      dataTab.label === "Chart" &&
                        downloadChartTableCSV(tablePar);
                      dataTab.label === "Summary" &&
                        downloadSummaryCSV(tablePar);
                      dataTab.label === "Table" &&
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={selectedInterval.isTime}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  secondYAxisLabel={"%"}
                  legends={getLegends()}
                  xDataKey={xDataKey}
                  xTick={
                    selectedInterval.isTime && selectedBlock.length === 0
                      ? (value: any, index: any) => {
                          return renderHourTick(value, 10);
                        }
                      : undefined
                  }
                  secondXDataKey={secondDataKey()}
                  defaultSelectedLegends={selectedLegends}
                  onLegendSelect={handleLegendSelect}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && <ChartTable {...tablePar} />}
            </>
          )}
        </div>
        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip note={<p>HPPC</p>} text=" ">
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
